/** Les trois tons livrés par le designer. */
export type Ton = 'couleur' | 'encre' | 'inverse'

interface Props {
  /** Côté du carré, en pixels. La marque est toujours carrée. */
  taille?: number
  /**
   * Étiquette accessible. Absente, la marque est une DÉCORATION.
   *
   * C'est le cas de loin le plus fréquent : le mot « Haicademy » est presque
   * toujours à côté, en vrai texte, et l'annoncer une seconde fois est du
   * bruit. Même convention que `ui/Icone.tsx`.
   */
  titre?: string
  ton?: Ton
  className?: string
}

/**
 * La marque seule — 06 §8, [DECISIONS §295].
 *
 * **Sans le mot.** C'est la forme du rail, de l'onglet du navigateur et de
 * l'icône d'application — trois endroits où le logotype complet ne serait
 * qu'un pâté. Partout où le nom doit s'apprendre, on pose `Logotype`.
 *
 * **Une image, et non plus un tracé.** Jusqu'au 20 août 2026, cette marque
 * était dessinée en SVG dans le code — un `H` qui monte, décrit dans
 * `geometry.ts`. Le designer a livré la vraie : une ampoule au-dessus d'un
 * livre ouvert. On ne redessine pas une marque de mémoire ; on sert ses
 * fichiers.
 *
 * **Le ton se choisit, il ne se teinte pas.** `currentColor` ne s'applique pas
 * à une image : les trois déclinaisons sont produites en amont. `couleur` — le
 * bleu de marque — est le défaut, parce qu'il se lit sur fond clair comme sur
 * fond sombre ; `encre` et `inverse` servent quand le fond est franchement
 * l'un ou l'autre.
 */
export function Marque({
  taille = 32,
  titre,
  ton = 'couleur',
  className = '',
}: Props) {
  const decoratif = titre === undefined

  return (
    <img
      src={`/marque/marque-${ton}-${String(sourceLaPlusProche(taille))}.png`}
      width={taille}
      height={taille}
      className={className}
      alt={titre ?? ''}
      aria-hidden={decoratif ? true : undefined}
      // Dans une ligne en flex, la marque ne doit jamais être écrasée par un
      // titre long — elle deviendrait une tache illisible.
      style={{ flexShrink: 0 }}
    />
  )
}

/** Les tailles produites, dans l'ordre croissant. */
const PRODUITES = [16, 32, 48, 64, 128, 180, 192, 256, 512]

/**
 * La source servie fait AU MOINS le double de la taille demandée.
 *
 * Un écran à densité double afficherait sinon une image agrandie, c'est-à-dire
 * floue — et c'est le cas de tous les téléphones auxquels ce produit s'adresse.
 */
function sourceLaPlusProche(taille: number): number {
  return PRODUITES.find((t) => t >= taille * 2) ?? 512
}
